import { Link } from 'react-router-dom';
import { ArrowRight, Target } from 'lucide-react';
import { useMyGoals } from '../../hooks/useMyHome';
import type { MyGoal } from '../../hooks/useMyHome';
import Spinner from '../ui/Spinner';

const FRAMEWORK_BADGE: Record<string, string> = {
  OKR: 'bg-blue-100 text-blue-700',
  KPI: 'bg-purple-100 text-purple-700',
  MIXED: 'bg-gray-100 text-gray-700',
};

function formatPeriod(start: string, end: string) {
  const s = new Date(start);
  const e = new Date(end);
  return `${s.getMonth() + 1}/${s.getDate()} ~ ${e.getMonth() + 1}/${e.getDate()}`;
}

function GoalCard({ goal }: { goal: MyGoal }) {
  return (
    <Link
      to="/goals"
      className="flex-shrink-0 w-64 bg-white border border-gray-200 rounded-lg p-4 hover:border-primary-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <h4 className="text-sm font-medium text-gray-900 line-clamp-2 leading-snug">{goal.title}</h4>
        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${FRAMEWORK_BADGE[goal.frameworkType] ?? FRAMEWORK_BADGE.MIXED}`}>
          {goal.frameworkType}
        </span>
      </div>

      {/* 진행률 */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-gray-500">진행률</span>
          <span className="text-sm font-medium text-gray-700">{Math.round(goal.progress)}%</span>
        </div>
        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-500 rounded-full transition-all"
            style={{ width: `${Math.min(100, goal.progress)}%` }}
          />
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-400">
        <span>{goal.departmentName}</span>
        <span>{formatPeriod(goal.periodStart, goal.periodEnd)}</span>
      </div>
    </Link>
  );
}

export default function GoalCards() {
  const { data: goals, isLoading } = useMyGoals();

  if (isLoading) return <Spinner />;

  if (!goals || goals.length === 0) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-gray-400">
        <Target className="size-4" />
        <span>참여 중인 목표가 없습니다</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
        {goals.map((goal) => (
          <GoalCard key={goal.goalId} goal={goal} />
        ))}
      </div>
      {goals.length >= 5 && (
        <Link to="/goals" className="inline-flex items-center gap-1 mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium">
          더보기 <ArrowRight className="size-3" />
        </Link>
      )}
    </div>
  );
}
